import { Alert, AlertDescription } from '@/components/ui/alert';
import { Insight } from '@/api/analytics';
import { Sparkles, Clock, CalendarDays } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface InsightsPanelProps {
  insights: Insight[];
  period: string;
  includeShared: boolean;
}

export function InsightsPanel({ insights, period, includeShared }: InsightsPanelProps) {
  const navigate = useNavigate();

  if (insights.length === 0) {
    return null;
  }

  const isActionInsight = (message: string) => {
    return message.includes('action') && message.includes('over 30 days');
  };

  const handleInsightClick = (insight: Insight) => {
    if (isActionInsight(insight.message)) {
      // Calculate bounded age range based on period
      let maxDays: number;
      if (period === '30d') {
        maxDays = 30;
      } else if (period === '90d') {
        maxDays = 90;
      } else {
        // 'all' = last year (365 days)
        maxDays = 365;
      }
      
      const boundedAgeRange = `30-${maxDays} days`;
      navigate(`/app/actions?ageRange=${encodeURIComponent(boundedAgeRange)}&includeShared=${includeShared}`);
    }
  };

  const getAlertStyles = (type: Insight['type']) => {
    switch (type) {
      case 'warning':
        return 'border-violet-500/50 bg-violet-500/10 text-violet-900 dark:text-violet-200';
      case 'success':
        return 'border-indigo-500/50 bg-indigo-500/10 text-indigo-900 dark:text-indigo-200';
      case 'info':
      default:
        return 'border-purple-500/50 bg-purple-500/10 text-purple-900 dark:text-purple-200';
    }
  };

  const getIcon = (type: Insight['type'], message: string) => {
    // Determine icon based on type and message content
    if (type === 'success') {
      return <Sparkles className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />;
    }
    
    // For warnings, use different icons based on message content
    if (message.includes('action')) {
      return <Clock className="h-5 w-5 text-violet-600 dark:text-violet-400" />;
    }
    
    return <CalendarDays className="h-5 w-5 text-violet-600 dark:text-violet-400" />;
  };

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">
        Insights
      </h3>
      <div className="grid gap-3 md:grid-cols-2">
        {insights.map((insight, index) => {
          const clickable = isActionInsight(insight.message);
          return (
            <Alert 
              key={index} 
              className={`${getAlertStyles(insight.type)} ${clickable ? 'cursor-pointer hover:bg-violet-500/20 transition-colors' : ''}`}
              onClick={() => clickable && handleInsightClick(insight)}
            >
              <AlertDescription className="flex items-center gap-3">
                {getIcon(insight.type, insight.message)}
                <span>{insight.message}</span>
              </AlertDescription>
            </Alert>
          );
        })}
      </div>
    </div>
  );
}
